import { useState, useCallback, useMemo } from 'react';
import { message } from 'antd';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import mapeamentosService from '../services/dashboard/mapeamentos.service';
import { 
  Questionario, 
  TipoRelatorio, 
  FormatoRelatorio,
  UseMapeamentosRelatorioReturn,
  FiltroQuestionarios,
  EstatisticasGerais,
  ValidacaoRelatorio
} from '../types/dashboard/mapeamentos.types';

export interface RelatorioParams {
  clienteId?: string;
  questionarioId?: string;
  tipo?: TipoRelatorio;
  incluir_dados_sensiveis?: boolean;
  incluir_observacoes?: boolean;
  incluir_estatisticas?: boolean;
  titulo_personalizado?: string;
  observacoes_adicionais?: string;
}

export interface RelatorioCompletoParams {
  clienteId?: string;
  clienteNome?: string;
  tipos_selecionados: TipoRelatorio[];
  questionario_id?: string;
  data_inicio?: string;
  data_fim?: string;
  incluir_dados_sensiveis: boolean;
  incluir_observacoes: boolean;
  incluir_estatisticas: boolean;
  titulo_personalizado?: string;
  observacoes_adicionais?: string;
}

export interface UseMapeamentosRelatorioReturnExtended extends UseMapeamentosRelatorioReturn {
  gerarRelatorioCompleto: (params: RelatorioCompletoParams) => Promise<void>;
}

export const useMapeamentosRelatorio = (): UseMapeamentosRelatorioReturnExtended => {
  // Estados principais
  const [relatorioModalVisible, setRelatorioModalVisible] = useState(false);
  const [loadingQuestionarios, setLoadingQuestionarios] = useState(false);
  const [questionarios, setQuestionarios] = useState<Questionario[]>([]);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [clienteRelatorio, setClienteRelatorio] = useState<string>('');

  // Controle do modal
  const openRelatorioModal = useCallback(async (clienteId?: string) => {
    setRelatorioModalVisible(true);
    
    // Sempre definir o cliente do relatório
    if (clienteId && clienteId.trim() !== '') {
      setClienteRelatorio(clienteId);
    } else {
      setClienteRelatorio('');
    }
    
    // Sempre recarregar questionários quando abrir o modal
    await loadQuestionarios({ cliente_id: clienteId });
  }, []);

  const closeRelatorioModal = useCallback(() => {
    setRelatorioModalVisible(false);
    setClienteRelatorio('');
    setQuestionarios([]); // Limpar questionários ao fechar
    mapeamentosService.cancelAllRequests();
  }, []);
  
  // Carregar questionários filtrados por cliente
  const loadQuestionarios = useCallback(async (filtros: FiltroQuestionarios = {}) => {
    if (loadingQuestionarios) {
      return;
    }

    setLoadingQuestionarios(true);
    
    try {
      const response = await mapeamentosService.listarQuestionarios(filtros);
      
      let questionariosArray: Questionario[] = [];
      if (Array.isArray(response)) {
        questionariosArray = response;
      } else if (response && typeof response === 'object' && 'data' in response) {
        questionariosArray = (response as any).data || [];
      } else {
        console.warn('Resposta em formato inesperado:', response);
        questionariosArray = [];
      }
      
      setQuestionarios(questionariosArray);
    } catch (error: any) {
      console.error('Erro ao carregar questionários:', error);
      message.error('Erro ao carregar questionários: ' + (error.message || 'Erro desconhecido'));
      setQuestionarios([]);
    } finally {
      setLoadingQuestionarios(false);
    }
  }, [loadingQuestionarios, clienteRelatorio]);

  // Gerar relatório específico de questionário
  const handleGerarRelatorio = useCallback(async (
    questionarioId: string, 
    formato: FormatoRelatorio = FormatoRelatorio.PDF,
    clienteId?: string
  ) => {
    if (generatingReport) return;
    
    setGeneratingReport(true);
    const messageKey = `generating-${questionarioId}-${formato}`;
    
    try {
      message.loading({ 
        content: `Preparando visualização do relatório ${formato.toUpperCase()}...`, 
        key: messageKey, 
        duration: 0 
      });
      
      // Gerar dados do relatório
      const config = {
        tipo: 'questionario_detalhado',
        questionario_id: questionarioId,
        cliente_id: clienteId,
        incluir_dados_sensiveis: true
      };

      const dadosRelatorio = await mapeamentosService.gerarDadosRelatorio(config);
      
      if (formato === FormatoRelatorio.PDF) {
        await gerarPDFRelatorio(dadosRelatorio.dados || dadosRelatorio, config);
        message.destroy(messageKey);
        message.success('Relatório PDF aberto em nova aba!');
        setRelatorioModalVisible(false);
      } else {
        message.destroy(messageKey);
        message.success(`Relatório ${formato.toUpperCase()} gerado!`);
      }
      
    } catch (error: any) {
      message.destroy(messageKey);
      const errorMessage = error.message || 'Erro ao gerar relatório';
      message.error(errorMessage);
      console.error('Erro ao gerar relatório:', error);
    } finally {
      setGeneratingReport(false);
    }
  }, [generatingReport]);

  // Gerar relatório rápido
  const gerarRelatorioRapido = useCallback(async (
    tipo: TipoRelatorio,
    params: RelatorioParams = {}
  ) => {
    const messageKey = `quick-report-${tipo}`;
    
    try {
      message.loading({ 
        content: 'Preparando visualização do relatório...', 
        key: messageKey, 
        duration: 0 
      });

      const config = {
        tipo,
        questionario_id: params.questionarioId,
        cliente_id: params.clienteId,
        incluir_dados_sensiveis: params.incluir_dados_sensiveis ?? true
      };

      const dadosRelatorio = await mapeamentosService.gerarDadosRelatorio(config);

      await gerarPDFRelatorio(dadosRelatorio.dados || dadosRelatorio, config);

      message.destroy(messageKey);
      message.success('Relatório aberto em nova aba!');
    } catch (error: any) {
      message.destroy(messageKey);
      console.error('Erro ao gerar relatório rápido:', error);
      message.error('Erro ao gerar relatório: ' + (error.message || 'Erro desconhecido'));
    }
  }, []);

  // Gerar PDF do relatório
  const gerarPDFRelatorio = useCallback(async (dados: any, config: any) => {
    if (!dados || (typeof dados === 'object' && Object.keys(dados).length === 0)) {
      console.warn('Dados vazios para PDF, gerando relatório básico');
    }

    const { jsPDF } = await import('jspdf');
    await import('jspdf-autotable');
    
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
      compress: true
    }) as any;

    const pageConfig = {
      width: doc.internal.pageSize.width,
      height: doc.internal.pageSize.height,
      margin: 20,
      footerHeight: 25
    };

    let currentY = pageConfig.margin;

    // Cabeçalho
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    
    const titulos = {
      'geral': 'Relatório Geral - Mapeamento LGPD',
      'questionario_detalhado': 'Relatório Detalhado - Questionário LGPD',
      'titulares': 'Relatório de Titulares - LGPD',
      'estatisticas': 'Relatório de Estatísticas - LGPD'
    };
    
    const titulo = titulos[config.tipo as keyof typeof titulos] || 'Relatório LGPD';
    doc.text(titulo, pageConfig.width / 2, currentY, { align: 'center' });
    currentY += 20;

    // Adicionar informação do cliente se disponível
    if (config.cliente_id) {
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.text(`Cliente ID: ${config.cliente_id}`, pageConfig.width / 2, currentY, { align: 'center' });
      currentY += 10;
    }

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const agora = new Date();
    const dataGeracao = `Gerado em: ${agora.toLocaleDateString('pt-BR')} às ${agora.toLocaleTimeString('pt-BR')}`;
    doc.text(dataGeracao, pageConfig.width / 2, currentY, { align: 'center' });
    currentY += 15;

    doc.setDrawColor(200, 200, 200);
    doc.line(pageConfig.margin, currentY, pageConfig.width - pageConfig.margin, currentY);
    currentY += 15;

    // Conteúdo baseado no tipo
    try {
      switch (config.tipo) {
        case 'geral':
          currentY = adicionarConteudoGeral(doc, dados, pageConfig, currentY);
          break;
        case 'questionario_detalhado':
          currentY = adicionarConteudoQuestionario(doc, dados, pageConfig, currentY);
          break;
        case 'titulares':
          currentY = adicionarConteudoTitulares(doc, dados, pageConfig, currentY);
          break;
        case 'estatisticas':
          currentY = adicionarConteudoEstatisticas(doc, dados, pageConfig, currentY);
          break;
        default:
          // Fallback para dados desconhecidos
          doc.setFontSize(12);
          doc.text('Dados do relatório não disponíveis ou formato não reconhecido.', pageConfig.margin, currentY);
          currentY += 20;
          break;
      }
    } catch (error) {
      console.error('Erro ao gerar conteúdo do PDF:', error);
      doc.setFontSize(12);
      doc.text('Erro ao processar dados do relatório.', pageConfig.margin, currentY);
      currentY += 20;
    }

    // Adicionar informações de debug se não há dados
    if (!dados || (typeof dados === 'object' && Object.keys(dados).length === 0)) {
      doc.setFontSize(10);
      doc.setTextColor(150, 150, 150);
      doc.text('Relatório gerado sem dados específicos do cliente.', pageConfig.margin, currentY);
      doc.text('Verifique se há dados cadastrados para este cliente.', pageConfig.margin, currentY + 10);
    }

    // Rodapé
    adicionarRodape(doc, pageConfig);

    // Abrir em nova aba
    const pdfBlob = new Blob([doc.output('arraybuffer')], { type: 'application/pdf' });
    const url = URL.createObjectURL(pdfBlob);
    window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 30000);
  }, []);

  // Funções auxiliares para gerar conteúdo do PDF
  const adicionarConteudoGeral = useCallback((doc: any, dados: any, pageConfig: any, currentY: number): number => {
    if (dados.estatisticas || dados.questionarios || dados.titulares) {
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('ESTATÍSTICAS GERAIS', pageConfig.margin, currentY);
      currentY += 15;

      const estatisticas = dados.estatisticas || {};
      const estatisticasData = [
        ['Métrica', 'Valor'],
        ['Total de Questionários', (dados.questionarios?.length || estatisticas.total_questionarios || 0).toString()],
        ['Questionários Concluídos', (estatisticas.questionarios_concluidos || 0).toString()],
        ['Total de Titulares', (dados.titulares?.length || estatisticas.total_titulares || 0).toString()],
        ['Dados Sensíveis', (estatisticas.dados_sensiveis || 0).toString()],
        ['Dados Comuns', (estatisticas.dados_comuns || 0).toString()]
      ];

      doc.autoTable({
        startY: currentY,
        head: [estatisticasData[0]],
        body: estatisticasData.slice(1),
        theme: 'striped',
        headStyles: { fillColor: [66, 139, 202] },
        margin: { left: pageConfig.margin, right: pageConfig.margin }
      });

      currentY = doc.lastAutoTable.finalY + 20;
    } else {
      doc.setFontSize(12);
      doc.text('Nenhuma estatística disponível para este cliente.', pageConfig.margin, currentY);
      currentY += 20;
    }

    return currentY;
  }, []);

  const adicionarConteudoQuestionario = useCallback((doc: any, dados: any, pageConfig: any, currentY: number): number => {
    if (dados.questionario) {
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('INFORMAÇÕES DO QUESTIONÁRIO', pageConfig.margin, currentY);
      currentY += 15;

      const infoData = [
        ['Campo', 'Valor'],
        ['Nome', dados.questionario.nome || 'N/A'],
        ['Abordagem', dados.questionario.abordagem || 'N/A'],
        ['Status', dados.questionario.inativo ? 'Inativo' : 'Ativo'],
        ['Data de Criação', dados.questionario.created_at ? new Date(dados.questionario.created_at).toLocaleDateString('pt-BR') : 'N/A']
      ];

      doc.autoTable({
        startY: currentY,
        head: [infoData[0]],
        body: infoData.slice(1),
        theme: 'striped',
        headStyles: { fillColor: [66, 139, 202] },
        margin: { left: pageConfig.margin, right: pageConfig.margin }
      });

      currentY = doc.lastAutoTable.finalY + 20;

      // Adicionar informações de capítulos se disponível
      if (dados.capitulos && dados.capitulos.length > 0) {
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text(`CAPÍTULOS (${dados.capitulos.length})`, pageConfig.margin, currentY);
        currentY += 15;

        const capitulosData = [['Capítulo', 'Áreas']];
        dados.capitulos.forEach((cap: any) => {
          capitulosData.push([
            cap.capitulo_id?.description || 'N/A',
            cap.areas_id?.length > 0 ? cap.areas_id.map((a: any) => a.description).join(', ') : 'Nenhuma'
          ]);
        });

        doc.autoTable({
          startY: currentY,
          head: [capitulosData[0]],
          body: capitulosData.slice(1),
          theme: 'striped',
          headStyles: { fillColor: [52, 152, 219] },
          margin: { left: pageConfig.margin, right: pageConfig.margin }
        });

        currentY = doc.lastAutoTable.finalY + 20;
      }
    } else {
      doc.setFontSize(12);
      doc.text('Informações do questionário não disponíveis.', pageConfig.margin, currentY);
      currentY += 20;
    }

    return currentY;
  }, []);

  const adicionarConteudoTitulares = useCallback((doc: any, dados: any, pageConfig: any, currentY: number): number => {
    if (dados.titulares && dados.titulares.length > 0) {
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text(`RELATÓRIO DE TITULARES (${dados.titulares.length})`, pageConfig.margin, currentY);
      currentY += 20;

      const titularesLimitados = dados.titulares.slice(0, 15); // Aumentar limite
      
      const titularesData = [
        ['Categoria', 'Quantidade', 'Faixa Etária', 'Dados']
      ];

      titularesLimitados.forEach((titular: any) => {
        titularesData.push([
          titular.categoria || 'N/A',
          titular.quantidade || 'N/A',
          titular.faixa_etaria?.join(', ') || 'N/A',
          titular.dados?.length ? `${titular.dados.length} tipo(s)` : '0'
        ]);
      });

      doc.autoTable({
        startY: currentY,
        head: [titularesData[0]],
        body: titularesData.slice(1),
        theme: 'striped',
        headStyles: { fillColor: [76, 175, 80] },
        margin: { left: pageConfig.margin, right: pageConfig.margin }
      });

      currentY = doc.lastAutoTable.finalY + 15;

      if (dados.titulares.length > 15) {
        doc.setFontSize(10);
        doc.setFont('helvetica', 'italic');
        doc.text(`... e mais ${dados.titulares.length - 15} titulares.`, pageConfig.margin, currentY);
        currentY += 10;
      }
    } else {
      doc.setFontSize(12);
      doc.text('Nenhum titular de dados cadastrado para este cliente.', pageConfig.margin, currentY);
      currentY += 20;
    }

    return currentY;
  }, []);

  const adicionarConteudoEstatisticas = useCallback((doc: any, dados: any, pageConfig: any, currentY: number): number => {
    const stats = dados.estatisticas_gerais || dados.estatisticas || {};

    if (Object.keys(stats).length > 0) {
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('ESTATÍSTICAS DE CONFORMIDADE LGPD', pageConfig.margin, currentY);
      currentY += 20;

      const estatisticasData = [
        ['Métrica', 'Valor', 'Status'],
        ['Taxa de Conclusão', `${stats.taxa_conclusao || 0}%`, stats.taxa_conclusao >= 80 ? 'Bom' : 'Atenção'],
        ['Total de Questionários', stats.total_questionarios?.toString() || '0', '-'],
        ['Total de Titulares', stats.total_titulares?.toString() || '0', '-'],
        ['Dados Sensíveis', stats.dados_sensiveis?.toString() || '0', stats.dados_sensiveis > 0 ? 'Atenção' : 'OK'],
        ['Dados Comuns', stats.dados_comuns?.toString() || '0', '-'],
        ['Áreas Mapeadas', stats.areas_mapeadas?.toString() || '0', '-']
      ];

      doc.autoTable({
        startY: currentY,
        head: [estatisticasData[0]],
        body: estatisticasData.slice(1),
        theme: 'striped',
        headStyles: { fillColor: [156, 39, 176] },
        margin: { left: pageConfig.margin, right: pageConfig.margin }
      });

      currentY = doc.lastAutoTable.finalY + 20;
    } else {
      doc.setFontSize(12);
      doc.text('Nenhuma estatística disponível.', pageConfig.margin, currentY);
      currentY += 20;
    }

    return currentY;
  }, []);

  const adicionarRodape = useCallback((doc: any, pageConfig: any): void => {
    const totalPages = doc.internal.getNumberOfPages();
    const agora = new Date();
    
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      
      const rodapeY = pageConfig.height - pageConfig.footerHeight + 5;
      
      doc.setDrawColor(200, 200, 200);
      doc.line(pageConfig.margin, rodapeY, pageConfig.width - pageConfig.margin, rodapeY);
      
      doc.setFontSize(8);
      doc.setTextColor(120, 120, 120);
      
      doc.text(`Página ${i} de ${totalPages}`, pageConfig.margin, rodapeY + 10);
      doc.text(`Gerado em ${agora.toLocaleDateString('pt-BR')}`, pageConfig.width - pageConfig.margin, rodapeY + 10, { align: 'right' });
      doc.text('Sistema de Mapeamento LGPD', pageConfig.width / 2, rodapeY + 10, { align: 'center' });
    }
  }, []);

  // Gerar relatório completo
  const gerarRelatorioCompleto = useCallback(async (params: RelatorioCompletoParams) => {
    if (generatingReport) return;
    
    setGeneratingReport(true);
    const messageKey = 'generating-complete-report';
    
    try {
      if (params.tipos_selecionados.length === 0) {
        throw new Error('Selecione pelo menos um tipo de relatório');
      }

      if (params.tipos_selecionados.includes(TipoRelatorio.QUESTIONARIO_DETALHADO) && !params.questionario_id) {
        throw new Error('Selecione um questionário para o relatório detalhado');
      }

      message.loading({ 
        content: 'Coletando dados do cliente...', 
        key: messageKey, 
        duration: 0 
      });

      // Buscar dados para cada tipo selecionado
      const promessasDados = params.tipos_selecionados.map(async (tipo) => {
        try {
          const config = {
            tipo,
            questionario_id: tipo === TipoRelatorio.QUESTIONARIO_DETALHADO ? params.questionario_id : undefined,
            cliente_id: params.clienteId,
            incluir_dados_sensiveis: params.incluir_dados_sensiveis
          };

          const dadosTipo = await mapeamentosService.gerarDadosRelatorio(config);
          
          const dadosValidados = dadosTipo?.dados || dadosTipo || {};
          
          return { tipo, dados: dadosValidados };
        } catch (error) {
          console.warn(`Erro ao buscar dados para ${tipo}:`, error);
          return { tipo, dados: {} }; // Retornar objeto vazio em vez de null
        }
      });

      const resultadosDados = await Promise.all(promessasDados);
      
      message.loading({ 
        content: 'Gerando arquivo PDF...', 
        key: messageKey, 
        duration: 0 
      });

      // Gerar PDF completo
      const pdf = await gerarPDFCompleto(resultadosDados, params);
      
      const pdfBlob = new Blob([pdf.output('arraybuffer')], { type: 'application/pdf' });
      
      // Abrir em nova aba
      const url = URL.createObjectURL(pdfBlob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 30000);
      
      message.destroy(messageKey);
      message.success('Relatório completo aberto com sucesso!');
      setRelatorioModalVisible(false);

    } catch (error: any) {
      message.destroy(messageKey);
      console.error('Erro ao gerar relatório completo:', error);
      message.error('Erro ao gerar relatório completo: ' + (error.message || 'Erro desconhecido'));
    } finally {
      setGeneratingReport(false);
    }
  }, [generatingReport]);

  const gerarPDFCompleto = useCallback(async (
    resultadosDados: Array<{ tipo: TipoRelatorio; dados: any }>,
    params: RelatorioCompletoParams
  ): Promise<jsPDF> => {
    const { jsPDF } = await import('jspdf');
    await import('jspdf-autotable');
    
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
      compress: true
    }) as any;

    const pageConfig = {
      width: doc.internal.pageSize.width,
      height: doc.internal.pageSize.height,
      margin: 20,
      footerHeight: 25
    };

    let currentY = pageConfig.margin;

    // Cabeçalho
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    const titulo = params.titulo_personalizado || 'Relatório Completo - Mapeamento LGPD';
    doc.text(titulo, pageConfig.width / 2, currentY, { align: 'center' });
    currentY += 20;

    if (params.clienteNome && params.clienteNome !== 'Cliente') {
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.text(`Cliente: ${params.clienteNome}`, pageConfig.width / 2, currentY, { align: 'center' });
      currentY += 10;
    }

    const agora = new Date();
    const dataGeracao = `Gerado em: ${agora.toLocaleDateString('pt-BR')} às ${agora.toLocaleTimeString('pt-BR')}`;
    doc.setFontSize(10);
    doc.text(dataGeracao, pageConfig.width / 2, currentY, { align: 'center' });
    currentY += 15;

    doc.setDrawColor(200, 200, 200);
    doc.line(pageConfig.margin, currentY, pageConfig.width - pageConfig.margin, currentY);
    currentY += 15;

    // Gerar cada seção
    let secaoNumero = 1;
    for (const { tipo, dados } of resultadosDados) {
      if (currentY > pageConfig.height - 100) {
        doc.addPage();
        currentY = pageConfig.margin;
     }

     try {
       doc.setFontSize(16);
       doc.setFont('helvetica', 'bold');
       doc.text(`${secaoNumero}. ${tipo.toUpperCase().replace('_', ' ')}`, pageConfig.margin, currentY);
       currentY += 20;

       switch (tipo) {
         case TipoRelatorio.GERAL:
           currentY = adicionarConteudoGeral(doc, dados, pageConfig, currentY);
           break;
         case TipoRelatorio.QUESTIONARIO_DETALHADO:
           currentY = adicionarConteudoQuestionario(doc, dados, pageConfig, currentY);
           break;
         case TipoRelatorio.TITULARES:
           currentY = adicionarConteudoTitulares(doc, dados, pageConfig, currentY);
           break;
         case TipoRelatorio.ESTATISTICAS:
           currentY = adicionarConteudoEstatisticas(doc, dados, pageConfig, currentY);
           break;
       }

       secaoNumero++;
       currentY += 20;
     } catch (error) {
       console.error(`Erro ao gerar seção ${tipo}:`, error);
       doc.setFontSize(12);
       doc.text(`Erro ao processar dados da seção ${tipo}`, pageConfig.margin, currentY);
       currentY += 30;
     }
   }

   // Observações adicionais
   if (params.observacoes_adicionais) {
     if (currentY > pageConfig.height - 100) {
       doc.addPage();
       currentY = pageConfig.margin;
     }

     doc.setFontSize(14);
     doc.setFont('helvetica', 'bold');
     doc.text('OBSERVAÇÕES ADICIONAIS', pageConfig.margin, currentY);
     currentY += 15;

     doc.setFontSize(10);
     doc.setFont('helvetica', 'normal');
     const observacoes = doc.splitTextToSize(params.observacoes_adicionais, pageConfig.width - 2 * pageConfig.margin);
     doc.text(observacoes, pageConfig.margin, currentY);
   }

   // Adicionar rodapé
   adicionarRodape(doc, pageConfig);

   return doc;
 }, [adicionarConteudoGeral, adicionarConteudoQuestionario, adicionarConteudoTitulares, adicionarConteudoEstatisticas, adicionarRodape]);

 // Gerar nome do arquivo
 const gerarNomeArquivoCompleto = useCallback((params: RelatorioCompletoParams): string => {
   const agora = new Date();
   const timestamp = agora.toISOString().slice(0, 19).replace(/[:.]/g, '-');
   
   let prefixo = 'relatorio-mapeamento';
   if (params.tipos_selecionados.length === 1) {
     prefixo += `-${params.tipos_selecionados[0]}`;
   } else {
     prefixo += '-completo';
   }
   
   if (params.clienteNome && params.clienteNome !== 'Cliente') {
     const nomeCliente = params.clienteNome.replace(/[^a-zA-Z0-9]/g, '-').substring(0, 20);
     prefixo += `-${nomeCliente}`;
   }
   
   return `${prefixo}-${timestamp}.pdf`;
 }, []);

 // Visualizar relatório
 const visualizarRelatorio = useCallback(async (
   tipo: string, 
   params: RelatorioParams
 ) => {
   try {
     let tipoEnum: TipoRelatorio;
     switch (tipo.toLowerCase()) {
       case 'dashboard':
       case 'geral':
        tipoEnum = TipoRelatorio.GERAL;
        break;
      case 'questionario':
      case 'questionario_detalhado':
        tipoEnum = TipoRelatorio.QUESTIONARIO_DETALHADO;
        break;
      case 'titulares':
        tipoEnum = TipoRelatorio.TITULARES;
        break;
      case 'estatisticas':
        tipoEnum = TipoRelatorio.ESTATISTICAS;
        break;
      default:
        throw new Error(`Tipo de relatório inválido: ${tipo}`);
    }

    await gerarRelatorioRapido(tipoEnum, params);
  } catch (error: any) {
    console.error('Erro ao visualizar relatório:', error);
    message.error('Erro ao visualizar relatório: ' + (error.message || 'Erro desconhecido'));
    throw error;
  }
}, [gerarRelatorioRapido]);

// Validar dados do relatório
const validarDadosRelatorio = useCallback(async (
  tipo: TipoRelatorio,
  questionarioId?: string,
  clienteId?: string
): Promise<ValidacaoRelatorio> => {
  try {
    const response = await mapeamentosService.validarDadosRelatorio({
      tipo,
      questionario_id: questionarioId,
      cliente_id: clienteId
    });
    
    return {
      valido: response.valido ?? true,
      avisos: response.avisos || [],
      erros: response.erros || [],
      estatisticas_previas: response.estatisticas_previas || {},
      recomendacoes: response.recomendacoes || []
    };
  } catch (error: any) {
    console.warn('Erro na validação (não crítico):', error);
    return {
      valido: true,
      avisos: ['Não foi possível validar os dados, mas o relatório pode ser gerado'],
      erros: [],
      estatisticas_previas: {},
      recomendacoes: []
    };
  }
}, []);

// Obter estatísticas para preview
const obterEstatisticasPreview = useCallback(async (clienteId?: string): Promise<EstatisticasGerais> => {
  try {
    const response = await mapeamentosService.obterEstatisticas(clienteId);
    
    if (response && response.success && response.data) {
      return response.data;
    }
    
    console.warn('Resposta de estatísticas preview vazia');
    return {
      total_questionarios: 0,
      questionarios_concluidos: 0,
      total_titulares: 0,
      categorias_titulares: 0,
      dados_sensiveis: 0,
      dados_comuns: 0,
      areas_mapeadas: 0,
      total_processos: 0,
      total_perguntas: 0,
      taxa_conclusao: 0
    };
  } catch (error: any) {
    console.warn('Erro ao obter estatísticas preview:', error);
    return {
      total_questionarios: 0,
      questionarios_concluidos: 0,
      total_titulares: 0,
      categorias_titulares: 0,
      dados_sensiveis: 0,
      dados_comuns: 0,
      areas_mapeadas: 0,
      total_processos: 0,
      total_perguntas: 0,
      taxa_conclusao: 0
    };
  }
}, []);

// Memoizar o retorno para evitar re-renders desnecessários
return useMemo(() => ({
  // Estados principais
  relatorioModalVisible,
  loadingQuestionarios,
  questionarios,
  generatingReport,

  // Funções principais
  openRelatorioModal,
  closeRelatorioModal,
  loadQuestionarios,
  handleGerarRelatorio,
  gerarRelatorioRapido,
  visualizarRelatorio,

  // Função para relatório completo
  gerarRelatorioCompleto,

  // Funções auxiliares
  validarDadosRelatorio,
  obterEstatisticasPreview
}), [
  relatorioModalVisible,
  loadingQuestionarios,
  questionarios,
  generatingReport,
  openRelatorioModal,
  closeRelatorioModal,
  loadQuestionarios,
  handleGerarRelatorio,
  gerarRelatorioRapido,
  visualizarRelatorio,
  gerarRelatorioCompleto,
  validarDadosRelatorio,
  obterEstatisticasPreview
]);
};

export default useMapeamentosRelatorio;